import { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { Form } from '@musinsa/pbo-core'
import PageHeader from '@/components/pbo/PageHeader'
import { PboScope } from '@/components/pbo/PboScope'
import { notice } from '@/components/pbo/notice'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useGetShipperOptions, useGetWarehouseNameOptions } from '@/services/inventory/stockLedger/useStockLedgerQueries'
import { DAILY_CLOSE_HEADS_EXCEL_URL, DAILY_CLOSE_ITEMS_EXCEL_URL } from '@/services/inventory/dailyClose/endpoints'
import { useGetDailyCloseHeads, useGetDailyCloseItems } from '@/services/inventory/dailyClose/useDailyCloseQueries'
import type { DailyCloseDownloadReason, DailyCloseHead, DailyCloseHeadView, DailyCloseItemSearch } from '@/services/inventory/dailyClose/types'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { DETAIL_DEFAULT_PAGE_SIZE, DETAIL_MAX_PAGE_SIZE, SCREEN_TITLE } from './constants'
import { DailyCloseFilter } from './components/DailyCloseFilter'
import { DailyCloseTables } from './components/DailyCloseTables'
import { ExcelDownloadReasonModal } from './components/ExcelDownloadReasonModal'
import type { DailyCloseFormValues } from './filterToRequest'
import { toDailyCloseSearch, validateDailyCloseForm } from './filterToRequest'
import { DailyCloseSearchQuery, fromSearchQuery, toSearchQuery } from './searchQuery'

type ExcelTarget = 'head' | 'item'

function DailyClose() {
  const [form] = Form.useForm()
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<DailyCloseSearchQuery>()
  const scope = useMemo(() => withCoMingle(shipperScopeOf(AuthStorage.user.get()), true), [])
  const defaults = useMemo<DailyCloseFormValues>(() => ({
    shipperCode: defaultShipper(scope).shipperCode || undefined,
    closeDateRange: [dayjs(), dayjs()],
  }), [scope])
  const [initialValues] = useState<DailyCloseFormValues>(() => {
    const restored = fromSearchQuery(parsedQs, defaults)
    if (scope.locked) restored.shipperCode = scopedShipperCode(scope, restored.shipperCode)
    return restored
  })
  const hasRestoredSearch = Boolean(
    parsedQs.closeDateFrom || parsedQs.closeDateTo || parsedQs.shipperCode || parsedQs.warehouseName || parsedQs.logisticsServiceType,
  )
  const [headView, setHeadView] = useState<DailyCloseHeadView | null>(() => {
    if (!hasRestoredSearch || validateDailyCloseForm(initialValues)) return null
    return { search: toDailyCloseSearch(initialValues), seq: 1 }
  })
  const seqRef = useRef(headView?.seq ?? 0)
  const [itemRequest, setItemRequest] = useState<DailyCloseItemSearch | null>(null)
  const [excelTarget, setExcelTarget] = useState<ExcelTarget | null>(null)

  const headsQuery = useGetDailyCloseHeads(headView)
  const itemsQuery = useGetDailyCloseItems(itemRequest)
  const { options: allShipperOptions } = useGetShipperOptions()
  const { options: warehouseOptions } = useGetWarehouseNameOptions()
  const shipperOptions = useMemo(
    () => scope.locked
      ? selectableShippers(scope).map((shipper) => ({ value: shipper.shipperCode, label: shipper.shipperName || shipper.shipperCode }))
      : allShipperOptions,
    [allShipperOptions, scope],
  )
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '일마감조회', config: { method: 'post' }, errorToast: true })

  const handleSearch = useCallback((rawValues: DailyCloseFormValues) => {
    const values = scope.locked ? { ...rawValues, shipperCode: scopedShipperCode(scope, rawValues.shipperCode) } : rawValues
    const message = validateDailyCloseForm(values)
    if (message) { notice.invalid(message); return }

    const search = toDailyCloseSearch(values)
    seqRef.current += 1
    setHeadView({ search, seq: seqRef.current })
    setItemRequest(null)
    const query = toSearchQuery(values)
    MenuStorage.menuOnSession.setSearch(query)
    replaceQuery(query)
    menuSession.reload()
  }, [menuSession, replaceQuery, scope])

  const handleReset = useCallback(() => {
    // antd Form의 touched·검증 상태를 초기화한 뒤 현재 사용자 범위의 기본값으로 덮어쓴다.
    form.resetFields()
    form.setFieldsValue({
      ...defaults,
      warehouseName: undefined,
      logisticsServiceType: undefined,
    })
    MenuStorage.menuOnSession.setSearch({})
    replaceQuery({})
    menuSession.reload()
  }, [defaults, form, menuSession, replaceQuery])

  const handleHeadDoubleClick = useCallback((row: DailyCloseHead) => {
    if (!headView) return
    setItemRequest({
      ...headView.search,
      shipperCode: row.shipperCode,
      closeDateFrom: row.closeDate,
      closeDateTo: row.closeDate,
      warehouseName: row.warehouseName,
      page: 1,
      pageSize: DETAIL_DEFAULT_PAGE_SIZE,
    })
  }, [headView])

  const handleItemPage = useCallback((page: number, pageSize: number) => {
    setItemRequest((current) => {
      if (!current) return current
      const nextPageSize = Math.min(pageSize, DETAIL_MAX_PAGE_SIZE)
      return { ...current, page: nextPageSize === current.pageSize ? page : 1, pageSize: nextPageSize }
    })
  }, [])

  const handleExcelConfirm = useCallback((reason: DailyCloseDownloadReason) => {
    const target = excelTarget
    setExcelTarget(null)
    if (target === 'head' && headView) {
      onDownloadExcel(DAILY_CLOSE_HEADS_EXCEL_URL, { ...headView.search, ...reason }).catch(() => undefined)
    } else if (target === 'item' && itemRequest) {
      onDownloadExcel(DAILY_CLOSE_ITEMS_EXCEL_URL, { ...itemRequest, ...reason }).catch(() => undefined)
    }
  }, [excelTarget, headView, itemRequest, onDownloadExcel])

  const selectedHead = useMemo(
    () => itemRequest && { shipperCode: itemRequest.shipperCode, closeDate: itemRequest.closeDateFrom, warehouseName: itemRequest.warehouseName },
    [itemRequest],
  )

  const itemPageInfo = itemsQuery.data?.pageInfo ?? {
    page: itemRequest?.page ?? 1,
    size: itemRequest?.pageSize ?? DETAIL_DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  }

  return (
    <PboScope>
      <PageHeader title={SCREEN_TITLE} />
      <DailyCloseFilter
        form={form}
        initialValues={initialValues}
        shipperOptions={shipperOptions}
        warehouseOptions={warehouseOptions}
        canEditShipper={canSelectShipper(scope)}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <DailyCloseTables
        heads={headsQuery.data ?? []}
        items={itemsQuery.data?.items ?? []}
        itemPageInfo={itemPageInfo}
        selectedHead={selectedHead}
        isHeadLoading={headsQuery.isFetching}
        isItemLoading={itemsQuery.isFetching}
        isDownloading={isDownloading}
        onHeadDoubleClick={handleHeadDoubleClick}
        onItemPage={handleItemPage}
        onDownload={setExcelTarget}
      />
      <ExcelDownloadReasonModal open={excelTarget != null} onConfirm={handleExcelConfirm} onCancel={() => setExcelTarget(null)} />
    </PboScope>
  )
}

export default DailyClose
